import { MouseEvent, useState, useEffect } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FaTimesCircle } from 'react-icons/fa';
import { useForm } from 'react-hook-form';

import { useAppDispatch } from '../../app/hooks';
import {
  createRoleAction,
  CreateRoleActionResponse,
  role_field_options,
} from './roleSlice';
import { SelectScopesModal } from './SelectScopesModal';
import { Role, Scope } from '../../types';

const initialState: Required<Role> = {
  role_id: '',
  name: '',
  type: 'internal',
  title: '',
  state: 'enabled',
  description: '',
  scopes: [],
};

type Props = {
  tenant_id: string;
  successCallback: (result: CreateRoleActionResponse) => void;
  dismissCallback: () => void;
};

export function RoleCreateForm({ tenant_id, successCallback, dismissCallback }: Props) {
  const dispatch = useAppDispatch();
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const [show_modal, setShowModal] = useState(false);
  const { handleSubmit, register, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { ...initialState, role_id: `${tenant_id}/` },
  });
  const scopes = watch('scopes');
  const role_name = watch('name');

  useEffect(() => {
    setValue('role_id', `${tenant_id}/${role_name}`);
  }, [role_name, tenant_id, setValue]);

  const handleCreate = async(data: Role) => {
    let result = await dispatch(createRoleAction({
      tenant_id,
      body: { ...data, scopes },
    }));
    if (createRoleAction.fulfilled.match(result)) {
      successCallback(result.payload);
    } else {
      setApiError(result.error);
      const subscription = watch(() => {
        setApiError(undefined);
        subscription.unsubscribe();
      });
    }
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dismissCallback();
  };
  const handleRemoveScope = (scope_id: string) => {
    setValue('scopes', scopes.filter(current => current !== scope_id));
  };
  const openCreateScopeModal = async(e: MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };
  const handleCloseModal = (selected_scopes: Scope[]) => {
    setShowModal(false);
    const new_scope_ids = selected_scopes.map(scope => scope.scope_id);
    setValue('scopes', [...scopes, ...new_scope_ids]);
  };
  return (
    <form onSubmit={handleSubmit(handleCreate)}>
      <fieldset>
        <div className="mb-3 row">
          <label htmlFor="role_id" className="col-sm-3 col-form-label">ID</label>
          <div className="col-sm-6">
            <input disabled type="text" id="role_id" className="form-control-plaintext"
              { ...register('role_id') }
            />
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="role_name" className="col-sm-3 col-form-label">Name</label>
          <div className="col-sm-6">
            <input type="text" id="role_name" className="form-control"
              { ...register('name', role_field_options.name) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.name?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="role_type" className="col-sm-3 col-form-label">Type</label>
          <div className="col-sm-6">
            <select id="role_type" className="form-select"
              { ...register('type') }
            >
              <option value="internal">Internal</option>
              <option value="shared">Shared</option>
            </select>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.type?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="role_title" className="col-sm-3 col-form-label">Title</label>
          <div className="col-sm-6">
            <input type="text" id="role_title" className="form-control"
              { ...register('title', role_field_options.title) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.title?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="role_description" className="col-sm-3 col-form-label">Description</label>
          <div className="col-sm-6">
            <textarea id="role_description" className="form-control"
              { ...register('description', role_field_options.description) }
            >
            </textarea>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="role_state" className="col-sm-3 col-form-label">State</label>
          <div className="col-sm-6">
            <select id="role_state" className="form-select"
              { ...register('state') }
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <div className="d-flex justify-content-between py-4">
            <h3>{scopes.length || 'No'} assigned scopes</h3>
            <button className="btn btn-primary my-2" onClick={(e) => openCreateScopeModal(e)}>
              Add Scopes
            </button>
          </div>
          <div className="d-flex flex-column w-50">
            {scopes.map((scope_id) => {
              return (
                <div className="border px-1 mx-1 my-1 border-info rounded-3 d-flex justify-content-between"
                  key={scope_id}>
                  <span> {scope_id} </span>
                  <FaTimesCircle size="23" onClick={() => handleRemoveScope(scope_id)} />
                </div>
              );
            })}
          </div>
        </div>
        <div className="d-flex">
          <span>{api_error?.message ?? api_error?.name}</span>
        </div>
        <div className="d-flex my-3 justify-content-center">
          <button type="submit" className="btn btn-primary mx-3">Create</button>
          <button className="btn btn-light mx-1" onClick={(e) => handleCancel(e)}>Cancel</button>
        </div>
      </fieldset>
      <SelectScopesModal
        tenant_id={tenant_id}
        ignore_scopes={scopes}
        show={show_modal}
        onClose={handleCloseModal}
      />
    </form>
  );
}
